import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import type {
  ConnectionProfile,
  ConnectionProfileCreate,
  ConnectionProfileUpdate,
} from '@/types/api';

/**
 * Hooks für Connection-Profile (CRUD, Backend entsteht parallel).
 *
 * Endpunkte (abgestimmter Vertrag):
 *   GET    /systems/{system_id}/connection-profiles
 *   POST   /systems/{system_id}/connection-profiles
 *   PATCH  /connection-profiles/{profile_id}
 *   DELETE /connection-profiles/{profile_id}
 *
 * Invalidation nach Mutationen: der komplette `systems`-Baum. Das deckt die
 * Profil-Liste (`systems.detail.{id}.connection-profiles`), das System-Detail
 * UND die System-Listen ab — Letztere liefern `has_active_ssh_profile` mit,
 * das der Run-Wizard als Filter nutzt und das sich durch jede Profil-
 * Änderung verschieben kann.
 */

export function useConnectionProfiles(
  systemId: string | undefined
): ReturnType<typeof useQuery<ConnectionProfile[]>> {
  return useQuery<ConnectionProfile[]>({
    queryKey: queryKeys.systems.connectionProfiles(systemId ?? ''),
    queryFn: () =>
      api.get<ConnectionProfile[]>(`/systems/${systemId ?? ''}/connection-profiles`),
    enabled: Boolean(systemId),
  });
}

function invalidateSystems(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: queryKeys.systems.all });
}

export interface CreateConnectionProfileInput {
  systemId: string;
  body: ConnectionProfileCreate;
}

export function useCreateConnectionProfile(): ReturnType<
  typeof useMutation<ConnectionProfile, Error, CreateConnectionProfileInput>
> {
  const qc = useQueryClient();
  return useMutation<ConnectionProfile, Error, CreateConnectionProfileInput>({
    mutationFn: ({ systemId, body }) =>
      api.post<ConnectionProfile, ConnectionProfileCreate>(
        `/systems/${systemId}/connection-profiles`,
        body
      ),
    onSuccess: () => invalidateSystems(qc),
  });
}

export interface UpdateConnectionProfileInput {
  profileId: string;
  body: ConnectionProfileUpdate;
}

export function useUpdateConnectionProfile(): ReturnType<
  typeof useMutation<ConnectionProfile, Error, UpdateConnectionProfileInput>
> {
  const qc = useQueryClient();
  return useMutation<ConnectionProfile, Error, UpdateConnectionProfileInput>({
    mutationFn: ({ profileId, body }) =>
      api.patch<ConnectionProfile, ConnectionProfileUpdate>(
        `/connection-profiles/${profileId}`,
        body
      ),
    onSuccess: () => invalidateSystems(qc),
  });
}

export interface DeleteConnectionProfileInput {
  profileId: string;
}

export function useDeleteConnectionProfile(): ReturnType<
  typeof useMutation<void, Error, DeleteConnectionProfileInput>
> {
  const qc = useQueryClient();
  return useMutation<void, Error, DeleteConnectionProfileInput>({
    mutationFn: ({ profileId }) => api.delete(`/connection-profiles/${profileId}`),
    onSuccess: () => invalidateSystems(qc),
  });
}
